import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { 
  Upload, 
  Eye, 
  Save, 
  Zap, 
  DollarSign, 
  Tag, 
  Image, 
  Code,
  Sparkles,
  Crown,
  Lock
} from 'lucide-react'

interface TemplateData {
  name: string
  description: string
  category: string
  price: string
  tags: string[]
  features: string[]
  license: string
  isPremium: boolean
  previewImage: File | null
  sourceCode: string
  metadata: {
    version: string
    compatibility: string[]
    requirements: string[]
  }
}

export default function CreateTemplate() {
  const navigate = useNavigate()
  const { address, isConnected } = useWallet()
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const [templateData, setTemplateData] = useState<TemplateData>({
    name: '',
    description: '',
    category: 'music',
    price: '',
    tags: [],
    features: [],
    license: 'HDH Template License v1.0',
    isPremium: false,
    previewImage: null,
    sourceCode: '',
    metadata: {
      version: '1.0.0',
      compatibility: ['desktop', 'tablet', 'mobile'],
      requirements: []
    }
  })
  
  const [currentTag, setCurrentTag] = useState('')
  const [currentFeature, setCurrentFeature] = useState('')
  const [loading, setLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const categories = [
    { id: 'music', name: 'Music', icon: '🎵' },
    { id: 'artist', name: 'Artist', icon: '🎨' },
    { id: 'portfolio', name: 'Portfolio', icon: '💼' },
    { id: 'business', name: 'Business', icon: '🏢' },
    { id: 'blog', name: 'Blog', icon: '📝' },
    { id: 'ecommerce', name: 'E-commerce', icon: '🛒' }
  ]

  const licenses = [
    { id: 'hdh-standard', name: 'HDH Standard License', description: 'Basic usage rights' },
    { id: 'hdh-premium', name: 'HDH Premium License', description: 'Enhanced features and support' },
    { id: 'hdh-commercial', name: 'HDH Commercial License', description: 'Commercial use allowed' }
  ]

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setTemplateData(prev => ({ ...prev, previewImage: file }))
      
      // Create preview URL
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    }
  }

  const addTag = () => {
    if (currentTag.trim() && !templateData.tags.includes(currentTag.trim())) {
      setTemplateData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }))
      setCurrentTag('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setTemplateData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const addFeature = () => {
    if (currentFeature.trim() && !templateData.features.includes(currentFeature.trim())) {
      setTemplateData(prev => ({
        ...prev,
        features: [...prev.features, currentFeature.trim()]
      }))
      setCurrentFeature('')
    }
  }

  const removeFeature = (featureToRemove: string) => {
    setTemplateData(prev => ({
      ...prev,
      features: prev.features.filter(feature => feature !== featureToRemove)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!isConnected) {
      alert('Please connect your wallet to create templates')
      return
    }

    if (!templateData.name || !templateData.description || !templateData.price) {
      alert('Please fill in all required fields')
      return
    }

    setLoading(true)
    
    try {
      // In production, this would:
      // 1. Upload template files to IPFS or decentralized storage
      // 2. Create metadata JSON
      // 3. Mint template NFT on Sui blockchain
      // 4. Set up revenue sharing smart contract
      
      console.log('Creating template with data:', templateData)
      
      // Simulate blockchain transaction
      await new Promise(resolve => setTimeout(resolve, 3000))
      
      alert(`🎉 Template "${templateData.name}" created and minted successfully! You can now earn revenue from every purchase.`)
      
      navigate('/creator-dashboard')
    } catch (error) {
      console.error('Error creating template:', error)
      alert('Failed to create template. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
           style={{
             background: `
               linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
               radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
             `,
             backgroundSize: '100% 100%, 800px 800px, 900px 900px'
           }}>
        <div className="text-center">
          <Lock className="w-16 h-16 text-gold-400 mx-auto mb-6" />
          <h2 
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Wallet Required
          </h2>
          <p className="text-purple-200 mb-8 text-lg">
            Please connect your wallet to create and mint templates
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-gold-500 text-black px-8 py-4 rounded-lg hover:bg-gold-600 transition-colors font-semibold"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-purple-900 text-white py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-4xl md:text-6xl font-bold mb-6"
            style={{ 
              fontFamily: 'Georgia, serif',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              background: 'linear-gradient(45deg, #F59E0B, #8B5CF6, #EF4444)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Create Template
          </h1>
          <p className="text-xl text-purple-200 mb-8">
            Design, mint, and monetize your website templates on the blockchain
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-gold-400" />
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white mb-2">Template Name *</label>
                <input
                  type="text"
                  value={templateData.name}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                  placeholder="Enter template name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-white mb-2">Category *</label>
                <select
                  value={templateData.category}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-6">
              <label className="block text-white mb-2">Description *</label>
              <textarea
                value={templateData.description}
                onChange={(e) => setTemplateData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                placeholder="Describe your template and its features..."
                required
              />
            </div>
          </div>

          {/* Pricing & License */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-gold-400" />
              Pricing & License
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white mb-2">Price (SUI) *</label>
                <input
                  type="number"
                  value={templateData.price}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, price: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
              
              <div>
                <label className="block text-white mb-2">License Type</label>
                <select
                  value={templateData.license}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, license: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                >
                  {licenses.map((license) => (
                    <option key={license.id} value={license.id}>
                      {license.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-6 flex items-center gap-3">
              <input
                type="checkbox"
                id="isPremium"
                checked={templateData.isPremium}
                onChange={(e) => setTemplateData(prev => ({ ...prev, isPremium: e.target.checked }))}
                className="w-5 h-5 text-gold-600 focus:ring-gold-500 border-white/30 rounded bg-white/10"
              />
              <label htmlFor="isPremium" className="text-white flex items-center gap-2">
                <Crown className="w-5 h-5 text-gold-400" />
                Premium Template (Enhanced features and support)
              </label>
            </div>
          </div>

          {/* Preview Image */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Image className="w-6 h-6 text-gold-400" />
              Preview Image
            </h2>
            
            <div className="space-y-4">
              {previewUrl ? (
                <div className="relative">
                  <img
                    src={previewUrl}
                    alt="Template preview"
                    className="w-full max-w-md h-64 object-cover rounded-lg mx-auto"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setTemplateData(prev => ({ ...prev, previewImage: null }))
                      setPreviewUrl(null)
                      if (fileInputRef.current) fileInputRef.current.value = ''
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/30 rounded-lg p-12 text-center hover:border-gold-400 transition-colors cursor-pointer bg-white/5"
                >
                  <Upload className="w-12 h-12 text-gold-400 mx-auto mb-4" />
                  <p className="text-purple-200 mb-2">Click to upload preview image</p>
                  <p className="text-purple-300 text-sm">PNG, JPG up to 10MB</p>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Tag className="w-6 h-6 text-gold-400" />
              Tags
            </h2>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                  placeholder="Add a tag..."
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="bg-gold-500 text-black px-4 py-2 rounded-lg hover:bg-gold-600 transition-colors"
                >
                  Add
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {templateData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="text-purple-400 hover:text-red-400 transition-colors"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Zap className="w-6 h-6 text-gold-400" />
              Features
            </h2>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentFeature}
                  onChange={(e) => setCurrentFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                  placeholder="Add a feature..."
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="bg-gold-500 text-black px-4 py-2 rounded-lg hover:bg-gold-600 transition-colors"
                >
                  Add
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {templateData.features.map((feature, index) => (
                  <span
                    key={index}
                    className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {feature}
                    <button
                      type="button"
                      onClick={() => removeFeature(feature)}
                      className="text-green-400 hover:text-red-400 transition-colors"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4 justify-center">
            <button
              type="button"
              onClick={() => navigate('/template-marketplace')}
              className="px-8 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-gold-500 text-black rounded-lg hover:bg-gold-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                  Minting Template...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Mint Template NFT
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
